/**
 * Flat, screen-space-scaled glyph markers for the wall-sketch edit handles.
 *
 * Same contract as {@link SphereMarker} (screen-constant size via the viewer
 * 'update' event, depth-fade material, highlight/unHighlight, setScaleFactor,
 * setRenderOrder, dispose) but with 2D glyph geometry lying in the XY plane —
 * the layout persona edits walls top-down, where a shaped glyph communicates
 * the drag affordance far better than an anonymous ball:
 *
 *   - {@link TriangleHandleMarker} — width handles: a triangle pointing away
 *     from the wall body ("pull outward to thicken this side").
 *   - {@link BowHandleMarker} — curvature handle: an arc ribbon with an
 *     arrowhead at each end ("drag to bend the wall into an arc").
 *
 * Glyphs point along +X in local space; call `setDirection` each update to
 * aim them (rotation around Z only — they stay flat on the floor plane).
 */

import * as THREE from 'three';
import { DefaultColors } from '../defines';
import { Viewer } from '../viewer/viewer';
import { Utils } from '../utils';
import { createDepthFadeMeshMaterial } from '../materials/DepthFadeUtils';

/**
 * Structural interface shared by SphereMarker and the glyph markers — what
 * the edit session's hover-highlight code needs from any handle visual.
 */
export interface EditHandleVisual {
  highlight(): void;
  unHighlight(): void;
  setScaleFactor(scale: number): void;
}

/** Base: screen-constant scaling + depth-fade coloring for a flat glyph mesh. */
abstract class GlyphMarkerBase extends THREE.Object3D implements EditHandleVisual {
  viewer: Viewer;
  mesh: THREE.Mesh;
  private scaleFactor = 1;
  private color = DefaultColors.TEMPORARY_OBJECTS;
  private _boundUpdateScale: () => void;

  protected constructor(viewer: Viewer, geometry: THREE.BufferGeometry) {
    super();
    this.viewer = viewer;
    // DoubleSide: the glyph is a flat sheet, visible from above and below.
    this.mesh = new THREE.Mesh(geometry, createDepthFadeMeshMaterial(this.color, 1.0, THREE.DoubleSide));
    this.add(this.mesh);
    this._boundUpdateScale = () => this.updateScale();
    viewer.addEventListener('update', this._boundUpdateScale);
  }

  highlight(): void {
    const material = this.mesh.material as THREE.ShaderMaterial;
    material.uniforms.uColor.value = this.color.clone().multiplyScalar(0.75).addScalar(0.5);
  }

  unHighlight(): void {
    const material = this.mesh.material as THREE.ShaderMaterial;
    material.uniforms.uColor.value = this.color;
  }

  setColor(color: THREE.Color): void {
    this.color = color;
    (this.mesh.material as THREE.ShaderMaterial).uniforms.uColor.value = color;
  }

  setScaleFactor(scale: number): void {
    this.scaleFactor = scale;
  }

  setRenderOrder(order: number): void {
    this.renderOrder = order;
    this.mesh.renderOrder = order;
  }

  /** Aim the glyph's +X axis along (dx, dy) in the floor plane. */
  setDirection(dx: number, dy: number): void {
    this.rotation.z = Math.atan2(dy, dx);
  }

  /** Same screen-constant sizing as SphereMarker (15 px per local unit). */
  updateScale(cameraOverride?: THREE.PerspectiveCamera | THREE.OrthographicCamera, widthOverride?: number, heightOverride?: number): void {
    const camera = cameraOverride ?? this.viewer.scene.getActiveCamera();
    let clientWidth: number, clientHeight: number;
    if (widthOverride !== undefined && heightOverride !== undefined) {
      clientWidth = widthOverride;
      clientHeight = heightOverride;
    } else {
      const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
      clientWidth = renderAreaSize.width;
      clientHeight = renderAreaSize.height;
    }
    const distance = camera.position.distanceTo(this.getWorldPosition(new THREE.Vector3()));
    const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
    const scale = 15 / pr;
    this.scale.set(scale, scale, scale);
    this.mesh.scale.set(this.scaleFactor, this.scaleFactor, this.scaleFactor);
  }

  dispose(): void {
    this.viewer.removeEventListener('update', this._boundUpdateScale);
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

/** Flat triangle list → non-indexed BufferGeometry in the XY plane (z = 0). */
function flatGeometry(triangles: Array<[number, number]>): THREE.BufferGeometry {
  const positions = new Float32Array(triangles.length * 3);
  for (let i = 0; i < triangles.length; i++) {
    positions[i * 3] = triangles[i][0];
    positions[i * 3 + 1] = triangles[i][1];
    positions[i * 3 + 2] = 0;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return geometry;
}

/**
 * Width handle: a triangle pointing along +X ("pull outward"). Footprint is
 * sized to read at the same visual weight as the 0.4-radius handle sphere it
 * replaces (glyphs share the sphere's 15 px/unit screen scaling).
 */
export class TriangleHandleMarker extends GlyphMarkerBase {
  constructor(viewer: Viewer) {
    super(
      viewer,
      flatGeometry([
        [0.62, 0],
        [-0.3, 0.44],
        [-0.3, -0.44],
      ]),
    );
  }
}

/**
 * Curvature (bow) handle: an arc ribbon bulging toward +X with a small
 * arrowhead at each end — a "bend me" affordance. The ribbon's apex sits at
 * the local origin so the glyph hugs the handle's pick point.
 */
export class BowHandleMarker extends GlyphMarkerBase {
  constructor(viewer: Viewer) {
    // Sized so the whole glyph (ribbon + arrowheads) reads at roughly the
    // footprint of the handle sphere it replaces — the 11 px grab radius
    // around the handle position must cover most of the visible glyph.
    const R_MID = 0.55; // ribbon midline radius
    const HALF_WIDTH = 0.1; // ribbon half thickness
    const HALF_ANGLE = (55 / 180) * Math.PI; // ribbon spans ±55° around +X
    const SEGMENTS = 14;
    const ARROW_LEN = 0.26;
    const ARROW_HALF_WIDTH = 0.15;

    const tris: Array<[number, number]> = [];
    // Shift so the ribbon apex (midline at θ=0) lands on the local origin.
    const cx = -R_MID;
    const ringPoint = (theta: number, radius: number): [number, number] => [cx + radius * Math.cos(theta), radius * Math.sin(theta)];

    // Ribbon: quad strip along the arc, two triangles per segment.
    for (let i = 0; i < SEGMENTS; i++) {
      const t0 = -HALF_ANGLE + (i / SEGMENTS) * 2 * HALF_ANGLE;
      const t1 = -HALF_ANGLE + ((i + 1) / SEGMENTS) * 2 * HALF_ANGLE;
      const in0 = ringPoint(t0, R_MID - HALF_WIDTH);
      const in1 = ringPoint(t1, R_MID - HALF_WIDTH);
      const out0 = ringPoint(t0, R_MID + HALF_WIDTH);
      const out1 = ringPoint(t1, R_MID + HALF_WIDTH);
      tris.push(in0, out0, out1, in0, out1, in1);
    }

    // Arrowheads: one at each ribbon end, pointing along the arc tangent
    // (outward, continuing the curve), based across the ribbon width.
    for (const end of [1, -1] as const) {
      const theta = end * HALF_ANGLE;
      const tip = ringPoint(theta, R_MID);
      const radial: [number, number] = [Math.cos(theta), Math.sin(theta)];
      const tangent: [number, number] = [-Math.sin(theta) * end, Math.cos(theta) * end];
      tris.push(
        [tip[0] + tangent[0] * ARROW_LEN, tip[1] + tangent[1] * ARROW_LEN],
        [tip[0] + radial[0] * ARROW_HALF_WIDTH, tip[1] + radial[1] * ARROW_HALF_WIDTH],
        [tip[0] - radial[0] * ARROW_HALF_WIDTH, tip[1] - radial[1] * ARROW_HALF_WIDTH],
      );
    }

    super(viewer, flatGeometry(tris));
  }
}
